import React, { useState, useEffect } from 'react';
import { configurationService } from '../services/configurationService';
import ModuleConfigTab from './ModuleConfigTab';
import { ConfigGuide } from './ConfigGuide';
import { SettingsSearch } from './SettingsSearch';
import {
  MODULE_CONFIG_TABS,
  MODULE_CONFIG_DEFAULTS,
  ModuleConfigs,
  ModuleConfigTabId,
  mergeModuleConfigs,
} from './moduleConfigDefs';

interface ConfigurationData {
  hardware: {
    unitCost: number;
    utilization: number;
    profitMargin: number;
    rentalRate: number;
  };
  application: {
    payBackPeriod: number;
    developmentCost: number;
    profitMargin: number;
    unitsPerYear: number;
  };
  licenseDurationDiscount: {
    lessThan12Month: number;
    month13To24: number;
    month25To36: number;
    month37To60: number;
    perpetual: number;
  };
  otherCosts: {
    internetFee: number;
    awsCompute: number;
    awsStorage: number;
    liveModeInternetCloudMultiplier: number;
    offlineModeInternetCloudMultiplier: number;
    defaultSparePricingMode: 'hw_only' | 'hw_sw' | 'hw_sw_other';
    allowSparePricingModeOverride: boolean;
    maintenanceAndSupport: number;
    trainingRate: number;
    trainingQuickHours: number;
    trainingMediumHours: number;
    trainingLongHours: number;
    provisioningHourlyRate: number;
    onFieldSupportDailyRate: number;
    unitWeightKg?: number;
    shippingCostPerKg?: number;
  };
}

type TabId = 'hardware' | 'application' | 'licensing' | 'costs' | ModuleConfigTabId;

interface TabConfig {
  id: TabId;
  label: string;
  icon: string;
}

const TABS: TabConfig[] = [
  {
    id: 'hardware',
    label: 'Hardware',
    icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'
  },
  {
    id: 'application',
    label: 'Application',
    icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
  },
  {
    id: 'licensing',
    label: 'Duration Discount',
    icon: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z'
  },
  {
    id: 'costs',
    label: 'Other Costs',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  ...MODULE_CONFIG_TABS.map((tab) => ({ id: tab.id, label: tab.label, icon: tab.icon }))
];

interface ConfigurationFormData extends ModuleConfigs {
  name: string;
  description: string;
  hardware_config: {
    unitCost: number;
    utilization: number;
    profitMargin: number;
    rentalRate: number;
    routerUnitCost: number;
    starlinkHardwareCost: number;
    starlinkMonthlyCost: number;
    // Manual welding hardware, which costs more than the automated systems.
    manualUnitCost: number;
    manualUtilization: number;
    manualProfitMargin: number;
    manualRentalRate: number;
  };
  application_config: {
    payBackPeriod: number;
    developmentCost: number;
    profitMargin: number;
    unitsPerYear: number;
  };
  duration_discount_config: {
    lessThan12Month: number;
    month13To24: number;
    month25To36: number;
    month37To60: number;
    perpetual: number;
  };
  other_costs_config: {
    internetFee: number;
    awsCompute: number;
    awsStorage: number;
    liveModeInternetCloudMultiplier: number;
    offlineModeInternetCloudMultiplier: number;
    defaultSparePricingMode: 'hw_only' | 'hw_sw' | 'hw_sw_other';
    allowSparePricingModeOverride: boolean;
    maintenanceAndSupport: number;
    trainingRate: number;
    trainingQuickHours: number;
    trainingMediumHours: number;
    trainingLongHours: number;
    provisioningHourlyRate: number;
    onFieldSupportDailyRate: number;
    unitWeightKg: number;
    shippingCostPerKg: number;
  };
}

const Configuration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('hardware');
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<null | 'saved' | 'error'>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<ConfigurationFormData>({
    name: '',
    description: '',
    hardware_config: {
      unitCost: 0,
      utilization: 0,
      profitMargin: 0,
      rentalRate: 0,
      routerUnitCost: 0,
      starlinkHardwareCost: 0,
      starlinkMonthlyCost: 0,
      manualUnitCost: 0,
      manualUtilization: 0,
      manualProfitMargin: 0,
      manualRentalRate: 0
    },
    application_config: {
      payBackPeriod: 0,
      developmentCost: 0,
      profitMargin: 0,
      unitsPerYear: 0
    },
    duration_discount_config: {
      lessThan12Month: 0,
      month13To24: 0,
      month25To36: 0,
      month37To60: 0,
      perpetual: 0
    },
    other_costs_config: {
      internetFee: 0,
      awsCompute: 0,
      awsStorage: 0,
      liveModeInternetCloudMultiplier: 1,
      offlineModeInternetCloudMultiplier: 0.5,
      defaultSparePricingMode: 'hw_only',
      allowSparePricingModeOverride: true,
      maintenanceAndSupport: 0,
      trainingRate: 0,
      trainingQuickHours: 1,
      trainingMediumHours: 4,
      trainingLongHours: 8,
      provisioningHourlyRate: 0,
      onFieldSupportDailyRate: 0,
      unitWeightKg: 5.0,
      shippingCostPerKg: 0
    },
    ...MODULE_CONFIG_DEFAULTS
  });

  const handleInputChange = (section: keyof ConfigurationFormData, field: string, value: number | string | boolean) => {
    setConfig(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const isStringField =
        field === 'defaultSparePricingMode' || field === 'defaultServiceTier';
      const nextValue =
        typeof value === 'boolean'
          ? value
          : isStringField
            ? value
            : typeof value === 'string'
              ? parseFloat(value) || 0
              : value;

      return {
        ...prev,
        [section]: {
          ...prev[section] as Record<string, unknown>,
          [field]: nextValue
        }
      };
    });
  };

  const handleMultiplierChange = (arrivalType: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      nre_config: {
        ...prev.nre_config,
        difficultyMultipliers: {
          ...prev.nre_config.difficultyMultipliers,
          [arrivalType]: parseFloat(value) || 0
        }
      }
    }));
  };

  // The compute multiplier lives on the active module's own config block (one of
  // material/ndt/coating), unlike the difficulty map which is shared on nre.
  const handleComputeMultiplierChange = (
    configKey: keyof ModuleConfigs,
    arrivalType: string,
    value: string
  ) => {
    setConfig(prev => {
      const block = prev[configKey] as { computeMultiplierByFormat?: Record<string, number> };
      return {
        ...prev,
        [configKey]: {
          ...block,
          computeMultiplierByFormat: {
            ...(block.computeMultiplierByFormat ?? {}),
            [arrivalType]: parseFloat(value) || 0
          }
        }
      };
    });
  };

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      const response = await configurationService.getCurrentConfiguration();
      const apiConfig = response;
      setConfig({
        name: apiConfig.name || '',
        description: apiConfig.description || '',
        hardware_config: {
          unitCost: apiConfig.hardware_config.unitCost,
          utilization: apiConfig.hardware_config.utilization,
          profitMargin: apiConfig.hardware_config.profitMargin,
          rentalRate: apiConfig.hardware_config.rentalRate,
          routerUnitCost: apiConfig.hardware_config.routerUnitCost ?? 0,
          starlinkHardwareCost: apiConfig.hardware_config.starlinkHardwareCost ?? 0,
          starlinkMonthlyCost: apiConfig.hardware_config.starlinkMonthlyCost ?? 0,
          // An unset Manual block shows the shared values, which is exactly what
          // Manual units price at today. Saving them back changes no price; the
          // user then edits the Manual unit cost to make it differ.
          manualUnitCost: apiConfig.hardware_config.manualUnitCost ?? apiConfig.hardware_config.unitCost,
          manualUtilization: apiConfig.hardware_config.manualUtilization ?? apiConfig.hardware_config.utilization,
          manualProfitMargin: apiConfig.hardware_config.manualProfitMargin ?? apiConfig.hardware_config.profitMargin,
          manualRentalRate: apiConfig.hardware_config.manualRentalRate ?? apiConfig.hardware_config.rentalRate,
        },
        application_config: {
          payBackPeriod: apiConfig.application_config.payBackPeriod,
          developmentCost: apiConfig.application_config.developmentCost,
          profitMargin: apiConfig.application_config.profitMargin,
          unitsPerYear: apiConfig.application_config.unitsPerYear,
        },
        duration_discount_config: {
          lessThan12Month: apiConfig.duration_discount_config.lessThan12Month,
          month13To24: apiConfig.duration_discount_config.month13To24,
          month25To36: apiConfig.duration_discount_config.month25To36,
          month37To60: apiConfig.duration_discount_config.month37To60,
          perpetual: apiConfig.duration_discount_config.perpetual,
        },
        other_costs_config: {
          internetFee: apiConfig.other_costs_config.internetFee,
          awsCompute: apiConfig.other_costs_config.awsCompute ?? 0,
          awsStorage: apiConfig.other_costs_config.awsStorage ?? 0,
          liveModeInternetCloudMultiplier: apiConfig.other_costs_config.liveModeInternetCloudMultiplier ?? 1,
          offlineModeInternetCloudMultiplier: apiConfig.other_costs_config.offlineModeInternetCloudMultiplier ?? 0.5,
          defaultSparePricingMode: apiConfig.other_costs_config.defaultSparePricingMode ?? 'hw_only',
          allowSparePricingModeOverride: apiConfig.other_costs_config.allowSparePricingModeOverride ?? true,
          maintenanceAndSupport: apiConfig.other_costs_config.maintenanceAndSupport,
          trainingRate: apiConfig.other_costs_config.trainingRate,
          trainingQuickHours: apiConfig.other_costs_config.trainingQuickHours ?? 1,
          trainingMediumHours: apiConfig.other_costs_config.trainingMediumHours ?? 4,
          trainingLongHours: apiConfig.other_costs_config.trainingLongHours ?? 8,
          provisioningHourlyRate: apiConfig.other_costs_config.provisioningHourlyRate || 0,
          onFieldSupportDailyRate: apiConfig.other_costs_config.onFieldSupportDailyRate || 0,
          unitWeightKg: apiConfig.other_costs_config.unitWeightKg || 5.0,
          shippingCostPerKg: apiConfig.other_costs_config.shippingCostPerKg || 0
        },
        ...mergeModuleConfigs(apiConfig)
      });
    } catch (error) {
      console.error('Error fetching configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveResult(null);
      await configurationService.createConfiguration(config);
      await loadConfiguration();
      setSaveResult('saved');
      setTimeout(() => setSaveResult(null), 3000);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSaveResult('error');
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = (
    label: string,
    value: number,
    onChange: (value: string) => void,
    symbol: string = '',
    isPercentage: boolean = false,
    suffix: string = ''
  ) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-text-grey">{label}</label>
      <div className="relative rounded-md shadow-sm">
        {symbol && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-heritage-grey sm:text-sm">{symbol}</span>
          </div>
        )}
        <input
          type="number"
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => {
            const val = e.target.value;
            // Remove leading zeros but keep single zero
            const cleanedVal = val.replace(/^0+(?=\d)/, '');
            onChange(cleanedVal);
          }}
          className={`block w-full rounded-md border-[#E2E5EA] focus:border-hero-red focus:ring-hero-red sm:text-sm ${
            symbol ? 'pl-7' : 'pl-3'
          } ${(isPercentage || suffix) ? 'pr-12' : 'pr-3'} py-2`}
          min="0"
          step="any"
        />
        {(isPercentage || suffix) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-heritage-grey sm:text-sm">{isPercentage ? '%' : suffix}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    const moduleTab = MODULE_CONFIG_TABS.find((tab) => tab.id === activeTab);
    if (moduleTab) {
      return (
        <ModuleConfigTab
          tab={moduleTab}
          values={config[moduleTab.configKey] as unknown as Record<string, number | string>}
          multipliers={config.nre_config.difficultyMultipliers}
          computeMultipliers={
            (config[moduleTab.configKey] as { computeMultiplierByFormat?: Record<string, number> })
              .computeMultiplierByFormat ?? {}
          }
          onFieldChange={(key, value) => handleInputChange(moduleTab.configKey, key, value)}
          onMultiplierChange={handleMultiplierChange}
          onComputeMultiplierChange={(arrivalType, value) =>
            handleComputeMultiplierChange(moduleTab.configKey, arrivalType, value)
          }
        />
      );
    }

    switch (activeTab) {
      case 'hardware':
        return (
          <div className="space-y-6">
            <div className="text-sm text-heritage-grey mb-4">
              Configure hardware cost parameters. These values help determine pricing based on hardware costs, expected utilization, and rental calculations.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInput('Unit Cost', config.hardware_config.unitCost,
                (value) => handleInputChange('hardware_config', 'unitCost', value), '$')}
              {renderInput('Expected Utilization', config.hardware_config.utilization,
                (value) => handleInputChange('hardware_config', 'utilization', value), '', true)}
              {renderInput('Hardware Profit Margin', config.hardware_config.profitMargin,
                (value) => handleInputChange('hardware_config', 'profitMargin', value), '', true)}
              {renderInput('Monthly Rental Rate', config.hardware_config.rentalRate,
                (value) => handleInputChange('hardware_config', 'rentalRate', value), '', true)}
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-1">Manual Welding Hardware</h3>
              <p className="text-xs text-heritage-grey mb-4">Manual welding hardware costs more than the automated systems, so Manual units price off these rates instead of the ones above. The monthly rental is worked out the same way. Every other machine keeps using the values above.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInput('Unit Cost', config.hardware_config.manualUnitCost,
                  (value) => handleInputChange('hardware_config', 'manualUnitCost', value), '$')}
                {renderInput('Expected Utilization', config.hardware_config.manualUtilization,
                  (value) => handleInputChange('hardware_config', 'manualUtilization', value), '', true)}
                {renderInput('Hardware Profit Margin', config.hardware_config.manualProfitMargin,
                  (value) => handleInputChange('hardware_config', 'manualProfitMargin', value), '', true)}
                {renderInput('Monthly Rental Rate', config.hardware_config.manualRentalRate,
                  (value) => handleInputChange('hardware_config', 'manualRentalRate', value), '', true)}
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-1">Internet Hardware (Welding)</h3>
              <p className="text-xs text-heritage-grey mb-4">Routers: 1 per 4 devices (min 1), one-time. Starlink: one-time terminal + monthly subscription, charged only when Connected. All default 0.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInput('Router unit cost (one-time)', config.hardware_config.routerUnitCost,
                  (value) => handleInputChange('hardware_config', 'routerUnitCost', value), '$')}
                {renderInput('Starlink hardware cost (one-time)', config.hardware_config.starlinkHardwareCost,
                  (value) => handleInputChange('hardware_config', 'starlinkHardwareCost', value), '$')}
                {renderInput('Starlink monthly cost', config.hardware_config.starlinkMonthlyCost,
                  (value) => handleInputChange('hardware_config', 'starlinkMonthlyCost', value), '$')}
              </div>
            </div>

            <div className="mt-4 p-4 bg-page-bg rounded-md">
              <div className="text-sm text-text-grey">
                <span className="font-medium">Hardware Cost Breakdown:</span>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {(() => {
                    const uc = config.hardware_config.unitCost;
                    const marginPct = config.hardware_config.profitMargin;
                    const utilPct = config.hardware_config.utilization;
                    const rentalPct = config.hardware_config.rentalRate;
                    const m = marginPct / 100;
                    const u = utilPct / 100;
                    const r = rentalPct / 100;
                    // Gross margin: price = cost / (1 - margin), then divided by
                    // utilization to cover idle time, matching hardware.py.
                    const salePrice = m < 1 ? uc / (1 - m) : 0;
                    const billable = m < 1 && u > 0 ? salePrice / u : 0;
                    const monthly = billable * r;
                    const f = (n: number) => `$${n.toFixed(2)}`;
                    return (
                      <>
                        <li>Base hardware cost per unit: {f(uc)}</li>
                        <li>Sale price = cost / (1 - {marginPct}% margin) = {f(salePrice)}</li>
                        <li>Utilization-adjusted = {f(salePrice)} / {utilPct}% = {f(billable)}</li>
                        <li>Monthly rental = {f(billable)} x {rentalPct}% rental rate = {f(monthly)}</li>
                      </>
                    );
                  })()}
                </ul>
              </div>
              <div className="mt-3 text-xs text-heritage-grey">
                Note: Profit margin is a gross margin, so the sale price is cost / (1 - margin). The monthly rental is that price divided by utilization (to cover idle time), times the rental rate.
              </div>
            </div>
          </div>
        );
      
      case 'application':
        return (
          <div className="space-y-6">
            <div className="text-sm text-heritage-grey mb-4">
              Configure application cost recovery and pricing parameters. These values help determine the per-unit price needed to recover development costs and generate profit. Units can be either direct sales or rental subscriptions.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInput('Development Cost ($)', config.application_config.developmentCost,
                (value) => handleInputChange('application_config', 'developmentCost', value), '$')}
              {renderInput('Target Recovery Period (months)', config.application_config.payBackPeriod,
                (value) => handleInputChange('application_config', 'payBackPeriod', value))}
              {renderInput('Expected Units/Year (Sales or Rentals)', config.application_config.unitsPerYear,
                (value) => handleInputChange('application_config', 'unitsPerYear', value))}
              {renderInput('Target Profit Margin', config.application_config.profitMargin,
                (value) => handleInputChange('application_config', 'profitMargin', value), '', true)}
            </div>
            <div className="mt-4 p-4 bg-page-bg rounded-md">
              <div className="text-sm text-text-grey">
                <span className="font-medium">Cost Recovery Breakdown:</span>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {(() => {
                    const dev = config.application_config.developmentCost;
                    const payback = config.application_config.payBackPeriod;
                    const units = config.application_config.unitsPerYear;
                    const marginPct = config.application_config.profitMargin;
                    const m = marginPct / 100;
                    // Gross margin: price = cost / (1 - margin), the same
                    // convention as hardware and the cost-model modules.
                    const monthlyRecovery = payback > 0 ? dev / payback : 0;
                    const costPerUnit = units > 0 ? monthlyRecovery / units : 0;
                    const fee = m < 1 ? costPerUnit / (1 - m) : 0;
                    const f = (n: number) => `$${n.toFixed(2)}`;
                    return (
                      <>
                        <li>Monthly cost recovery: {f(dev)} / {payback} mo = {f(monthlyRecovery)} / month</li>
                        <li>Cost recovery per unit: {f(monthlyRecovery)} / {units} units per year = {f(costPerUnit)} / unit / month</li>
                        <li>Software fee = cost / (1 - {marginPct}% margin) = {f(fee)} / unit / month</li>
                      </>
                    );
                  })()}
                </ul>
              </div>
              <div className="mt-3 text-xs text-heritage-grey">
                Note: Profit margin is a gross margin, so the per-unit software fee is cost / (1 - margin). A unit can be a direct sale or an active rental subscription.
              </div>
            </div>
          </div>
        );

      case 'licensing':
        return (
          <div className="space-y-6">
            <div className="text-sm text-heritage-grey mb-4">
              Configure duration-based discounts. Longer duration licenses can have higher discount percentages.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInput('0-12 Months (Standard Rate)', config.duration_discount_config.lessThan12Month,
                (value) => handleInputChange('duration_discount_config', 'lessThan12Month', value), '', true)}
              {renderInput('13-24 Months Discount', config.duration_discount_config.month13To24,
                (value) => handleInputChange('duration_discount_config', 'month13To24', value), '', true)}
              {renderInput('25-36 Months Discount', config.duration_discount_config.month25To36,
                (value) => handleInputChange('duration_discount_config', 'month25To36', value), '', true)}
              {renderInput('37-60 Months Discount', config.duration_discount_config.month37To60,
                (value) => handleInputChange('duration_discount_config', 'month37To60', value), '', true)}
              {renderInput('Perpetual License Discount', config.duration_discount_config.perpetual,
                (value) => handleInputChange('duration_discount_config', 'perpetual', value), '', true)}
            </div>
            <div className="text-xs text-heritage-grey mt-2">
              Enter values as percentages (e.g., enter 5 for a 5% discount)
            </div>
          </div>
        );

      case 'costs':
        return (
          <div className="space-y-6">
            <div className="text-sm text-heritage-grey mb-4">
              Configure recurring and support-related costs. These values help determine additional operational costs per customer that need to be factored into the final pricing.
            </div>
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">Connectivity & Mode Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInput('Connectivity (per machine/month)', config.other_costs_config.internetFee,
                  (value) => handleInputChange('other_costs_config', 'internetFee', value), '$')}
                {renderInput('Cloud Compute (per machine/month)', config.other_costs_config.awsCompute,
                  (value) => handleInputChange('other_costs_config', 'awsCompute', value), '$')}
                {renderInput('Cloud Storage (per machine/month)', config.other_costs_config.awsStorage,
                  (value) => handleInputChange('other_costs_config', 'awsStorage', value), '$')}
                {renderInput('Live Mode Internet/Cloud Multiplier', config.other_costs_config.liveModeInternetCloudMultiplier,
                  (value) => handleInputChange('other_costs_config', 'liveModeInternetCloudMultiplier', value), '', false, 'x')}
                {renderInput('Offline Mode Internet/Cloud Multiplier', config.other_costs_config.offlineModeInternetCloudMultiplier,
                  (value) => handleInputChange('other_costs_config', 'offlineModeInternetCloudMultiplier', value), '', false, 'x')}
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">Spare Charging Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-grey">Default Spare Charging Mode</label>
                  <select
                    value={config.other_costs_config.defaultSparePricingMode}
                    onChange={(e) => handleInputChange('other_costs_config', 'defaultSparePricingMode', e.target.value)}
                    className="block w-full rounded-md border-[#E2E5EA] focus:border-hero-red focus:ring-hero-red sm:text-sm px-3 py-2"
                  >
                    <option value="hw_only">HW only</option>
                    <option value="hw_sw">HW + SW</option>
                    <option value="hw_sw_other">HW + SW + Infrastructure</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-grey">Allow Spare Mode Override in Proposal</label>
                  <select
                    value={config.other_costs_config.allowSparePricingModeOverride ? 'yes' : 'no'}
                    onChange={(e) => handleInputChange('other_costs_config', 'allowSparePricingModeOverride', e.target.value === 'yes')}
                    className="block w-full rounded-md border-[#E2E5EA] focus:border-hero-red focus:ring-hero-red sm:text-sm px-3 py-2"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">Service Rate Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInput('Hourly Support Rate', config.other_costs_config.maintenanceAndSupport,
                  (value) => handleInputChange('other_costs_config', 'maintenanceAndSupport', value), '$')}
                {renderInput('Training Hourly Rate', config.other_costs_config.trainingRate,
                  (value) => handleInputChange('other_costs_config', 'trainingRate', value), '$')}
                {renderInput('Training Starter Session Hours', config.other_costs_config.trainingQuickHours,
                  (value) => handleInputChange('other_costs_config', 'trainingQuickHours', value))}
                {renderInput('Training Standard Session Hours', config.other_costs_config.trainingMediumHours,
                  (value) => handleInputChange('other_costs_config', 'trainingMediumHours', value))}
                {renderInput('Training Full Day Session Hours', config.other_costs_config.trainingLongHours,
                  (value) => handleInputChange('other_costs_config', 'trainingLongHours', value))}
                {renderInput('Provisioning Hourly Rate', config.other_costs_config.provisioningHourlyRate,
                  (value) => handleInputChange('other_costs_config', 'provisioningHourlyRate', value), '$')}
                {renderInput('On-field Support Daily Rate', config.other_costs_config.onFieldSupportDailyRate,
                  (value) => handleInputChange('other_costs_config', 'onFieldSupportDailyRate', value), '$')}
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">Mobilization Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderInput('Unit Weight', config.other_costs_config.unitWeightKg,
                  (value) => handleInputChange('other_costs_config', 'unitWeightKg', value), '', false, 'kg')}
                {renderInput('Shipping Cost per kg', config.other_costs_config.shippingCostPerKg,
                  (value) => handleInputChange('other_costs_config', 'shippingCostPerKg', value), '$')}
              </div>
            </div>

            <div className="mt-4 p-4 bg-page-bg rounded-md">
              <div className="text-sm text-text-grey">
                <span className="font-medium">Monthly Operational Costs:</span>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>Fixed Monthly Costs: ${(config.other_costs_config.internetFee + config.other_costs_config.awsCompute + config.other_costs_config.awsStorage).toFixed(2)} <span className="text-heritage-grey">(Internet + Cloud charges)</span></li>
                  <li>Support Cost (8 hours): ${(config.other_costs_config.maintenanceAndSupport * 8).toFixed(2)} <span className="text-heritage-grey">(Based on standard 8-hour support)</span></li>
                  <li>Training Starter Session: ${(config.other_costs_config.trainingRate * config.other_costs_config.trainingQuickHours).toFixed(2)} <span className="text-heritage-grey">({config.other_costs_config.trainingQuickHours} hour session)</span></li>
                  <li>Training Standard Session: ${(config.other_costs_config.trainingRate * config.other_costs_config.trainingMediumHours).toFixed(2)} <span className="text-heritage-grey">({config.other_costs_config.trainingMediumHours} hour session)</span></li>
                  <li>Training Full Day Session: ${(config.other_costs_config.trainingRate * config.other_costs_config.trainingLongHours).toFixed(2)} <span className="text-heritage-grey">({config.other_costs_config.trainingLongHours} hour session)</span></li>
                  <li>Provisioning Cost (4 hours): ${(config.other_costs_config.provisioningHourlyRate * 4).toFixed(2)} <span className="text-heritage-grey">(Based on standard 4-hour provisioning)</span></li>
                  <li>On-field Support (1 day): ${(config.other_costs_config.onFieldSupportDailyRate).toFixed(2)} <span className="text-heritage-grey">(Daily rate)</span></li>
                </ul>

                <span className="font-medium block mt-4">Mobilization Cost Example:</span>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>For 2 units: ${(2 * config.other_costs_config.unitWeightKg * config.other_costs_config.shippingCostPerKg).toFixed(2)} <span className="text-heritage-grey">(2 units × {config.other_costs_config.unitWeightKg}kg × ${config.other_costs_config.shippingCostPerKg}/kg)</span></li>
                </ul>
              </div>
            </div>

            <div className="text-sm text-text-grey">
              <span className="font-medium">Cost Descriptions:</span>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Connectivity (per machine/month): Required for remote support and system connectivity</li>
                <li>Cloud Compute / Cloud Storage (per machine/month): Infrastructure costs for hosting and data storage</li>
                <li>Live/Offline Internet-Cloud Multipliers: Scale cloud/connectivity cost by selected operating mode</li>
                <li>Default Spare Charging Mode: Controls whether spare units include only hardware, hardware+software, or full costs</li>
                <li>Allow Spare Mode Override in Proposal: Lets sales adjust spare charging mode quote-by-quote</li>
                <li>Hourly Support Rate: Technical support and maintenance services per hour</li>
                <li>Training Hourly Rate: Customer training and onboarding services per hour</li>
                <li>Training Starter/Standard/Full Day Hours: Session lengths used for proposal training duration selection</li>
                <li>Provisioning Hourly Rate: System setup and provisioning services per hour</li>
                <li>On-field Support Daily Rate: Daily rate for onsite support services</li>
                <li>Unit Weight: Standard weight per unit for shipping calculations</li>
                <li>Shipping Cost per kg: Rate charged for shipping each kg of equipment</li>
              </ul>
              <p className="mt-4 text-heritage-grey italic">Note: Support and training costs are charged based on actual hours used. Monthly costs are fixed per customer installation.</p>
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return <div className="px-8 py-6 text-sm text-heritage-grey">Loading configuration…</div>;
  }

  return (
    <div className="pb-10">
      {/* Action bar: search (left) + save with inline status (right) */}
      <div className="px-8 pt-6 pb-4">
        <div className="flex justify-between items-center gap-4">
          <SettingsSearch onSelect={(tabId) => setActiveTab(tabId as TabId)} />
          <div className="flex items-center gap-3 shrink-0">
            {saveResult === 'saved' && <span className="text-[13px] font-medium text-galvanizing-green">Saved ✓</span>}
            {saveResult === 'error' && <span className="text-[13px] font-medium text-readable-red">Save failed — try again</span>}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-readable-red rounded-lg hover:bg-hero-red-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hero-red disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E2E5EA] bg-white/60">
        <div className="px-8">
          <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-hero-red text-readable-red'
                    : 'border-transparent text-heritage-grey hover:text-blueprint-blue hover:border-heritage-grey'
                  }
                `}
              >
                <svg
                  className={`-ml-0.5 mr-2 h-5 w-5 ${
                    activeTab === tab.id ? 'text-hero-red' : 'text-heritage-grey group-hover:text-heritage-grey'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content: form (left) + sticky guide / live preview (right) */}
      <div className="px-8 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
          <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6">
            {renderTabContent()}
          </div>
          <ConfigGuide
            activeTab={activeTab}
            hardware={config.hardware_config}
            application={config.application_config}
            costs={config.other_costs_config}
          />
        </div>
      </div>
    </div>
  );
};

export default Configuration; 
